// Keeps the cart page's free-shipping progress card in step with the cart. After every cart change
// (cart.js publishes PUB_SUB_EVENTS.cartUpdate) the section is re-read from the server, so the
// zone threshold, currency formatting and translated text all stay in Liquid; only the changed bits
// are copied across, which lets the fill bar tween instead of being re-rendered.
class CartFreeShipping extends HTMLElement {
  connectedCallback() {
    this.requestId = 0;
    if (this.dataset.mode === 'convert') this.apply(this);
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  refresh() {
    const requestId = ++this.requestId;
    const url = `${window.location.pathname}?section_id=${encodeURIComponent(this.dataset.sectionId)}`;
    fetch(url, { cache: 'no-store' })
      .then((response) => response.text())
      .then((html) => {
        if (requestId !== this.requestId) return;
        const next = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-free-shipping');
        this.apply(next);
      })
      .catch((error) => console.error(error));
  }

  // Currencies other than PLN/EUR: the threshold is converted in the browser from the market's
  // exchange rate, so state, progress and texts are derived here rather than copied from the server.
  convertedView(source) {
    const tools = window.CowalkiesFreeShipping;
    const currency = source.dataset.currency;
    const threshold = tools.convert(parseFloat(source.dataset.base));
    const total = (parseFloat(source.dataset.totalCents) || 0) / 100;
    const view = {
      caption: tools.fill(source.dataset.tplCaption, tools.format(threshold, currency), false),
    };
    if (source.dataset.empty === 'true') {
      view.state = 'hidden';
    } else if (total >= threshold) {
      view.state = 'free';
      view.progress = 100;
      view.headline = tools.fill(source.dataset.tplFree, '', false);
    } else {
      view.state = 'more';
      view.progress = Math.round((total / threshold) * 1000) / 10;
      view.headline = tools.fill(source.dataset.tplMore, tools.format(Math.ceil(threshold - total), currency), true);
    }
    return view;
  }

  apply(next) {
    const converted = next && next.dataset.mode === 'convert' && window.CowalkiesFreeShipping;
    const view = converted ? this.convertedView(next) : null;
    const state = view ? view.state : next ? next.dataset.state : 'hidden';
    const section = this.closest('.ai-cart-fs');
    if (section) section.classList.toggle('ai-card-stack', state !== 'hidden');
    this.hidden = state === 'hidden';
    if (!next || state === 'hidden') {
      this.dataset.state = 'hidden';
      return;
    }

    const headline = this.querySelector('[data-fs-headline]');
    const caption = this.querySelector('[data-fs-caption]');
    const track = this.querySelector('[data-fs-track]');

    // Server-rendered (PLN/EUR) text arrives as HTML; converted text arrives as nodes.
    const nextHeadline = document.createElement('div');
    const nextCaption = document.createElement('div');
    if (view) {
      nextHeadline.appendChild(view.headline);
      nextCaption.appendChild(view.caption);
    } else {
      nextHeadline.innerHTML = next.querySelector('[data-fs-headline]').innerHTML;
      nextCaption.textContent = next.querySelector('[data-fs-caption]').textContent;
    }
    const progress = view ? view.progress : next.dataset.progress;
    const first = !this.dataset.state || this.dataset.state === 'hidden';

    this.dataset.state = state;
    this.dataset.progress = progress;
    this.style.setProperty('--fs-progress', `${progress}%`);
    if (track) track.setAttribute('aria-valuenow', String(Math.floor(parseFloat(progress) || 0)));
    if (caption) caption.textContent = nextCaption.textContent;

    if (headline && headline.innerHTML !== nextHeadline.innerHTML) {
      if (first) {
        headline.innerHTML = nextHeadline.innerHTML;
      } else {
        // Quick fade out → swap → fade in, so the changing amount reads as one calm update.
        this.classList.add('is-swapping');
        window.setTimeout(() => {
          headline.innerHTML = nextHeadline.innerHTML;
          this.classList.remove('is-swapping');
        }, 160);
      }
    }
  }
}

if (!customElements.get('cart-free-shipping')) customElements.define('cart-free-shipping', CartFreeShipping);
